<template>
  <div>
    <SiteHero
      image="/media/kalkulator-hero.webp"
      eyebrow="Sprawdź opłaty"
      title="Kalkulator"
      lead="Oszacuj koszty zakupu na aukcji oraz bezpiecznego transportu statkiem do Europy."
      scroll-to="kalkulator"
    />

    <section id="kalkulator" class="site_section site_section--paper">
      <div class="site_container calculator_grid">
        <div>
          <h2>Kalkulator Kosztów Importu Auta z Japonii</h2>
          <p class="calculator_lead">
            Oblicz szacunkowe koszty sprowadzenia samochodu z Japonii do Europy. Wprowadź
            wartość pojazdu, aby poznać szczegółowe zestawienie opłat.
          </p>

          <router-link to="/kontakt/" class="site_button">Zapytaj o szczegóły kalkulacji</router-link>

          <SiteContactList class="calculator_contacts" :items="contacts" />
        </div>

        <div class="calculator">
          <div class="calculator_input">
            <label for="car-price">Cena samochodu na aukcji:</label>
            <span>
              <input id="car-price" v-model.number="price" type="number" min="0" step="10000" />
              JPY
            </span>
          </div>

          <p class="calculator_hint">
            Wpisz szacowaną wartość wylicytowanego pojazdu w jenach (JPY). Na tej podstawie
            zostaną obliczone opłaty aukcyjne, transport oraz cło.
          </p>

          <table class="summary">
            <tbody>
              <tr>
                <th scope="row">1. Opłaty aukcyjne</th>
                <td>
                  <span class="summary_pln">{{ pln(auctionFee * rate) }}</span>
                  <span class="summary_jpy">{{ jpy(auctionFee) }}</span>
                </td>
              </tr>
              <tr>
                <th scope="row">2. Opłata celna</th>
                <td><span class="summary_pln">{{ pln(customs * rate) }}</span></td>
              </tr>
              <tr>
                <th scope="row">3. Nasza prowizja</th>
                <td><span class="summary_pln">{{ pln(COMMISSION_PLN) }}</span></td>
              </tr>
              <tr class="summary_total">
                <th scope="row">Łączny koszt samochodu</th>
                <td><span class="summary_pln">{{ pln(total) }}</span></td>
              </tr>
            </tbody>
          </table>

          <p class="calculator_rate">
            <template v-if="rate">
              Kurs przeliczenia: <strong>1 JPY = {{ rate.toFixed(4) }} PLN</strong>
              (źródło: NBP, tabela A z dnia {{ rateDate }}). Kurs ma charakter informacyjny i
              może się różnić od kursu transakcyjnego.
            </template>
            <template v-else>
              Nie udało się pobrać kursu z NBP. Użyto kursu zastępczego:
              <strong>1 JPY = {{ FALLBACK_RATE.toFixed(4) }} PLN</strong>.
            </template>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// The import cost calculator. Everything the WordPress page did with an inline
// script and getElementById is reactive state here; the exchange rate comes
// from the Polish central bank. Wzory siedzą w @/utils/importCost, wspólne z
// kalkulatorem przy karcie aukcji.

import pageHead from '@/mixins/pageHead'
import { pageSchema } from '@/utils/schema'
import SiteHero from '@/components/SiteHero.vue'
import SiteContactList from '@/components/SiteContactList.vue'
import {
  FALLBACK_RATE,
  COMMISSION_PLN,
  auctionFee,
  customsFee,
  totalPln,
  pln,
  jpy,
  fetchJpyRate,
} from '@/utils/importCost'

export default {
  name: 'KalkulatorPage',
  components: { SiteHero, SiteContactList },
  mixins: [pageHead],

  pageHead: {
    title: 'Kalkulator - Japonia Cars',
    description:
      'Kalkulator kosztów importu auta z Japonii: opłaty aukcyjne, cło, transport i prowizja.',
    schema: pageSchema({
      path: '/kalkulator/',
      name: 'Kalkulator',
      breadcrumbs: [{ name: 'Kalkulator' }],
      published: '2026-05-25T10:47:20+00:00',
      modified: '2026-06-08T11:58:13+00:00',
    }),
  },

  data() {
    return {
      FALLBACK_RATE,
      COMMISSION_PLN,
      price: 0,
      rate: null,
      rateDate: '',
      contacts: [
        { icon: 'phone', title: 'Numer telefonu', value: '+48 509 230 500', href: 'tel:+48509230500' },
        { icon: 'mail', title: 'E-mail', value: 'kontakt@japoniacars.pl', href: 'mailto:kontakt@japoniacars.pl' },
        { icon: 'pin', title: 'Lokalizacja biura', value: 'ul. Wałowa 32, 43-100 Tychy' },
      ],
    }
  },

  computed: {
    auctionFee() {
      return auctionFee(this.price)
    },

    customs() {
      return customsFee(this.price)
    },

    total() {
      return totalPln(this.price, this.rate)
    },
  },

  async created() {
    const { rate, date } = await fetchJpyRate()
    this.rate = rate
    this.rateDate = date
  },

  methods: { pln, jpy },
}
</script>

<style scoped>
.calculator_grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: center;
}

.calculator_grid h2 {
  max-width: 549px;
  margin-bottom: 22.5px;
}

.calculator_lead {
  margin-bottom: 32px;
}

.calculator_contacts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 40px;
}

.calculator_contacts :deep(.contact_icon) {
  width: 62px;
  height: 62px;
  font-size: 32px;
}

.calculator_contacts :deep(li + li) {
  border-top: 0;
}

/* ---------------------------------------------------------- the panel --- */

.calculator {
  padding: 24px 32px;
  border-radius: var(--radius);
  background: #fff;
}

.calculator_input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.calculator_input label {
  color: #111;
  font-size: 21px;
  font-weight: 600;
}

.calculator_input span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}

.calculator_input input {
  width: 160px;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font: inherit;
}

.calculator_hint,
.calculator_rate {
  color: #aaa;
  font-size: 11px;
  line-height: 1.45;
}

.calculator_hint {
  margin-bottom: 10px;
}

.summary {
  width: 100%;
  margin-bottom: 10px;
  border-collapse: collapse;
  font-size: 14px;
}

.summary th {
  padding: 8px 0;
  color: var(--ink-body);
  font-weight: 400;
  text-align: left;
}

.summary td {
  padding: 8px 0;
  text-align: right;
}

.summary tr:first-child th,
.summary tr:first-child td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.summary_pln {
  display: block;
  color: #111;
}

.summary_jpy {
  display: block;
  color: #aaa;
  font-size: 11px;
}

.summary_total th,
.summary_total td {
  padding-top: 10px;
  font-weight: 700;
}

.summary_total .summary_pln {
  font-size: 17px;
  font-weight: 700;
}

@media (max-width: 1024px) {
  .calculator_grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

@media (max-width: 600px) {
  .calculator {
    padding: 20px;
  }

  .calculator_contacts {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .calculator_input {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
